import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Upload as UploadIcon,
  File,
  CheckCircle,
  XCircle,
  Play,
  FolderPlus,
  FileText,
  Trash2,
  Plus,
  Layers,
  Loader2,
  X,
  Pencil,
  TableProperties
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardHeader, CardTitle, CardContent } from '../components/ui/card';
import { projectAPI, metadataAPI } from '../services/api';

/**
 * BatchProjectUpload page — route: /project/batch/new
 *
 * Single-screen form for creating a new multi-batch analysis project.
 * The user fills in the project name, adds one or more batches (each with
 * ZIP files and an optional per-batch metadata CSV), and optionally uploads
 * a master metadata CSV. Clicking "Start Analysis" chains all API calls
 * in order:
 *   1. Create project  → obtain projectId
 *   2. Upload each batch's files (and optional metadata)
 *   3. Upload master metadata (if provided)
 *   4. Start batch sequencing → redirect to batch monitor
 */

// helper functions:
const makeBlankRow = (cols) => {
  const row = {};
  cols.forEach(c => { row[c.key] = ''; });
  return row;
};

// default columns
const MASTER_DEFAULT_COLS = [
  { key: 'barcode', label: 'barcode', required: true, locked: true },
  { key: 'sample_id', label: 'sample_id', required: true, locked: true },
  { key: 'folder', label: 'folder', required: false, locked: true },
  { key: 'sample_info', label: 'sample_info', required: false, locked: true },
  { key: 'sample_type', label: 'sample_type', required: false, locked: true },
  { key: 'date', label: 'date', required: false, locked: true },
  { key: 'submission', label: 'submission', required: false, locked: true },
  { key: 'hatchery', label: 'hatchery', required: false, locked: true },
  { key: 'sum', label: 'sum', required: false, locked: true },
  { key: 'status', label: 'status', required: false, locked: true },
  { key: 'vis_score', label: 'vis_score', required: false, locked: true },
  { key: 'sig_lesion', label: 'sig_lesion', required: false, locked: true },
  { key: 'weight', label: 'weight', required: false, locked: true },
  { key: 'length', label: 'length', required: false, locked: true },
  { key: 'number', label: 'number', required: false, locked: true },
  { key: 'read_count_total', label: 'read_count_total', required: false, locked: true },
  { key: 'unclassified', label: 'unclassified', required: false, locked: true },
  { key: 'bacteria', label: 'bacteria', required: false, locked: true },
];

export default function BatchProjectUpload() {
  const navigate = useNavigate();

  // Project details
  const [projectName, setProjectName] = useState('');
  // eslint-disable-next-line no-unused-vars
  const [projectDescription, setProjectDescription] = useState('');

  // Batches: array of { files: File[], metadata: File|null, uploaded: boolean }
  const [batches, setBatches] = useState([{ files: [], metadata: null, uploaded: false }]);
  const [currentBatchIndex, setCurrentBatchIndex] = useState(0);

  // Master metadata — uploaded file, in-app created sheet, or selected from DB
  const [masterMetadataFile, setMasterMetadataFile] = useState(null);
  const [masterMode, setMasterMode] = useState('upload'); // 'upload' | 'create' | 'select'
  const [masterEditorOpen, setMasterEditorOpen] = useState(false);
  // Sheets available to select from the metadata DB (master type only)
  const [dbSheets, setDbSheets] = useState([]);
  const [dbSheetsLoading, setDbSheetsLoading] = useState(false);
  const [selectedDbSheetId, setSelectedDbSheetId] = useState(null);
  const [masterCols, setMasterCols] = useState(MASTER_DEFAULT_COLS);
  const [masterRows, setMasterRows] = useState([makeBlankRow(MASTER_DEFAULT_COLS)]);

  // UI state
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [nameError, setNameError] = useState('');
  const [createdProjectId, setCreatedProjectId] = useState(null);

  // Submission progress
  const [submitPhase, setSubmitPhase] = useState('');   // Human-readable label for current step
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadingBatchIndex, setUploadingBatchIndex] = useState(null);

  // -------------------------------------------------------------------------
  // Helpers
  // -------------------------------------------------------------------------

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  };


  // -------------------------------------------------------------------------
  // Inline master metadata editor helpers
  // -------------------------------------------------------------------------

  const addMasterCol = () => {
    const key = `col_${Date.now()}`;
    setMasterCols(prev => [...prev, { key, label: '', required: false, locked: false }]);
    setMasterRows(prev => prev.map(r => ({ ...r, [key]: '' })));
  };

  const removeMasterCol = (key) => {
    setMasterCols(prev => prev.filter(c => c.key !== key));
    setMasterRows(prev => prev.map(r => { const { [key]: _, ...rest } = r; return rest; }));
  };

  const renameMasterCol = (key, label) => {
    setMasterCols(prev => prev.map(c => c.key === key ? { ...c, label } : c));
  };

  const addMasterRow = () => {
    const row = {};
    masterCols.forEach(c => { row[c.key] = ''; });
    setMasterRows(prev => [...prev, row]);
  };

  const removeMasterRow = (idx) => {
    if (masterRows.length <= 1) return;
    setMasterRows(prev => prev.filter((_, i) => i !== idx));
  };

  const updateMasterCell = (rowIdx, colKey, value) => {
    setMasterRows(prev => prev.map((r, i) => i === rowIdx ? { ...r, [colKey]: value } : r));
  };

  const handleMasterPaste = (e, rowIdx, colIdx) => {
    const text = e.clipboardData.getData('text/plain');
    if (!text.includes('\t') && !text.includes('\n')) return;
    e.preventDefault();
    const pastedRows = text.trimEnd().split('\n').map(l => l.split('\t'));
    setMasterRows(prev => {
      const updated = prev.map(r => ({ ...r }));
      pastedRows.forEach((pastedRow, offset) => {
        const targetIdx = rowIdx + offset;
        while (updated.length <= targetIdx) {
          const blank = {};
          masterCols.forEach(c => { blank[c.key] = ''; });
          updated.push(blank);
        }
        pastedRow.forEach((val, colOffset) => {
          const targetColIdx = colIdx + colOffset;
          if (targetColIdx >= masterCols.length) return;
          updated[targetIdx] = { ...updated[targetIdx], [masterCols[targetColIdx].key]: val.trim() };
        });
      });
      return updated;
    });
  };

  /** Convert the in-app editor state into a CSV string for upload. */
  const generateMasterCSV = () => {
    const header = masterCols.map(c => c.label || c.key).join(',');
    const rows = masterRows.map(r =>
      masterCols.map(c => `"${(r[c.key] || '').replace(/"/g, '""')}"`).join(',')
    );
    return [header, ...rows].join('\n');
  };

  // -------------------------------------------------------------------------
  // Batch management
  // -------------------------------------------------------------------------

  const handleBatchFileSelect = (e) => {
    const files = Array.from(e.target.files);
    setBatches(prev => {
      const updated = [...prev];
      updated[currentBatchIndex] = {
        ...updated[currentBatchIndex],
        files: [...updated[currentBatchIndex].files, ...files],
      };
      return updated;
    });
    e.target.value = '';
  };

  const handleBatchMetadataSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      setBatches(prev => {
        const updated = [...prev];
        updated[currentBatchIndex] = { ...updated[currentBatchIndex], metadata: file };
        return updated;
      });
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    const files = Array.from(e.dataTransfer.files);
    setBatches(prev => {
      const updated = [...prev];
      updated[currentBatchIndex] = {
        ...updated[currentBatchIndex],
        files: [...updated[currentBatchIndex].files, ...files],
      };
      return updated;
    });
  };

  const handleDragOver = (e) => e.preventDefault();

  const removeFile = (fileIndex) => {
    setBatches(prev => {
      const updated = [...prev];
      updated[currentBatchIndex] = {
        ...updated[currentBatchIndex],
        files: updated[currentBatchIndex].files.filter((_, i) => i !== fileIndex),
      };
      return updated;
    });
  };

  const addNewBatch = () => {
    setBatches(prev => [...prev, { files: [], metadata: null, uploaded: false }]);
    setCurrentBatchIndex(batches.length);
  };

  const removeBatch = (index) => {
    if (batches.length <= 1) return;
    setBatches(prev => prev.filter((_, i) => i !== index));
    if (currentBatchIndex >= index && currentBatchIndex > 0) {
      setCurrentBatchIndex(currentBatchIndex - 1);
    }
  };

  // -------------------------------------------------------------------------
  // Project name availability check (on blur)
  // -------------------------------------------------------------------------

  const handleNameBlur = async () => {
    if (!projectName.trim()) return;
    try {
      const res = await projectAPI.checkName(projectName.trim());
      setNameError(res.data.available ? '' : 'That project name already exists. Please choose a different one.');
    } catch {
      // silently ignore — the submit step will catch it if it persists
    }
  };

  // -------------------------------------------------------------------------
  // Unified submit — chains all API calls in order
  // -------------------------------------------------------------------------

  const handleSubmit = async () => {
    // Validate
    if (!projectName.trim()) {
      setError('Please enter a project name');
      return;
    }
    const emptyBatch = batches.find((b) => b.files.length === 0);
    if (emptyBatch) {
      const index = batches.indexOf(emptyBatch);
      setError(`Please select ZIP files for Batch ${index + 1}`);
      setCurrentBatchIndex(index);
      return;
    }
    if (!masterMetadataFile) {
      setError('Master metadata is required. Please upload a master_metadata.csv before starting analysis.');
      return;
    }

    setLoading(true);
    setError('');
    setSuccess('');

    try {
      // 1. Resolve master metadata to a File object
      let metadataToUpload = masterMetadataFile;
      if (masterMode === 'create') {
        const csvString = generateMasterCSV();
        const blob = new Blob([csvString], { type: 'text/csv' });
        metadataToUpload = new File([blob], 'master_metadata.csv', { type: 'text/csv' });
      } else if (masterMode === 'select') {
        setSubmitPhase('Exporting selected sheet...');
        const res = await metadataAPI.exportCSV(selectedDbSheetId);
        const blob = new Blob([res.data], { type: 'text/csv' });
        metadataToUpload = new File([blob], 'master_metadata.csv', { type: 'text/csv' });
      }

      // 3. Create project in DB — deferred to here so no orphaned records if the user
      //    abandons the form. If a previous attempt already created it, reuse that ID
      //    so retrying after a failed upload doesn't hit the "name already exists" error.
      let newProjectId = createdProjectId;
      if (!newProjectId) {
        setSubmitPhase('Creating project...');
        const projResponse = await projectAPI.create(projectName, projectDescription);
        newProjectId = projResponse.data.id;
        setCreatedProjectId(newProjectId);
      }

      // 4. Upload each batch's files and optional per-batch metadata
      for (let i = 0; i < batches.length; i++) {
        if (batches[i].uploaded) continue;

        setSubmitPhase(`Uploading batch ${i + 1} of ${batches.length}...`);
        setUploadingBatchIndex(i);
        setUploadProgress(0);

        await projectAPI.uploadBatchFiles(
          newProjectId,
          i + 1,
          batches[i].files,
          (progressEvent) => {
            setUploadProgress(Math.round((progressEvent.loaded * 100) / progressEvent.total));
          }
        );

        if (batches[i].metadata) {
          await projectAPI.uploadBatchMetadata(newProjectId, i + 1, batches[i].metadata);
        }

        // Only mark as uploaded after both files and metadata have succeeded
        setBatches(prev => {
          const updated = [...prev];
          updated[i] = { ...updated[i], uploaded: true };
          return updated;
        });
      }

      // 5. Upload master metadata
      setSubmitPhase('Uploading master metadata...');
      setUploadProgress(0);
      await projectAPI.uploadMasterMetadata(newProjectId, metadataToUpload, (progressEvent) => {
        setUploadProgress(Math.round((progressEvent.loaded * 100) / progressEvent.total));
      });

      // 6. Start sequencing
      setSubmitPhase('Starting sequencing pipeline...');
      const response = await projectAPI.startBatchSequencing(newProjectId);
      setSuccess('Analysis started! Redirecting...');
      setTimeout(() => {
        navigate(`/analysis/${response.data.parent_analysis_id}/manage`);
      }, 1500);

    } catch (err) {
      const detail = err.response?.data?.detail || '';
      if (detail.toLowerCase().includes('no space left') || detail.includes('28')) {
        setError('The server has run out of storage space. Please contact your administrator to free up disk space before trying again.');
      } else {
        setError(detail || 'Something went wrong. Please try again.');
      }
    } finally {
      setLoading(false);
      setSubmitPhase('');
      setUploadingBatchIndex(null);
      setUploadProgress(0);
    }
  };

  // -------------------------------------------------------------------------
  // Render
  // -------------------------------------------------------------------------

  return (
    <div className="container mx-auto px-4 py-12 max-w-3xl">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="text-center mb-10"
      >
        <h1 className="text-4xl font-bold mb-3">New Batch Analysis</h1>
        <p className="text-muted-foreground">Fill in the details below and click Start.</p>
      </motion.div>

      {/* Messages */}
      {success && (
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 text-green-800 dark:text-green-200 p-4 rounded-lg flex items-start gap-3 mb-6"
        >
          <CheckCircle className="h-5 w-5 mt-0.5 flex-shrink-0" />
          <span>{success}</span>
        </motion.div>
      )}

      {error && (
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          className="bg-destructive/10 border border-destructive/20 text-destructive p-4 rounded-lg flex items-start gap-3 mb-6"
        >
          <XCircle className="h-5 w-5 mt-0.5 flex-shrink-0" />
          <span>{error}</span>
        </motion.div>
      )}

      <div className="space-y-6">
        {/* Section 1: Project Details */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              <FolderPlus className="h-4 w-4" />
              Project Details
            </CardTitle>
          </CardHeader>
          <CardContent>
            <label className="text-sm font-medium">Project Name</label>
            <input
              type="text"
              value={projectName}
              onChange={(e) => { setProjectName(e.target.value); setNameError(''); }}
              onBlur={handleNameBlur}
              placeholder="e.g., Study 2024 Batches"
              disabled={loading}
              className={`w-full h-10 px-3 mt-1 rounded-md border bg-background disabled:opacity-50 ${nameError ? 'border-red-500' : 'border-input'}`}
            />
            {nameError && <p className="text-sm text-red-500 mt-1">{nameError}</p>}
          </CardContent>
        </Card>

        {/* Section 2: Batches */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="flex items-center gap-2 text-base">
                <Layers className="h-4 w-4" />
                Batches
              </CardTitle>
              <Button variant="outline" size="sm" onClick={addNewBatch} disabled={loading}>
                <Plus className="h-4 w-4 mr-1" /> Add Batch
              </Button>
            </div>
            <p className="text-sm text-muted-foreground">
              One ZIP file (or multiple ZIPs) per batch. Each batch can have an optional metadata CSV.
            </p>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Batch tabs */}
            <div className="flex flex-wrap gap-2">
              {batches.map((batch, index) => (
                <div key={index} className="flex items-center">
                  <button
                    onClick={() => setCurrentBatchIndex(index)}
                    disabled={loading}
                    className={`px-4 py-1.5 rounded-l-lg text-sm font-medium transition-colors disabled:opacity-50 ${currentBatchIndex === index
                      ? 'bg-primary text-primary-foreground'
                      : 'bg-muted hover:bg-muted/80'
                      } ${batch.uploaded ? 'border-l-4 border-l-green-500' : ''}`}
                  >
                    Batch {index + 1}
                    {batch.uploaded && <CheckCircle className="inline h-3 w-3 ml-1" />}
                  </button>
                  {batches.length > 1 && !batch.uploaded && (
                    <button
                      onClick={() => removeBatch(index)}
                      disabled={loading}
                      className="px-2 py-1.5 bg-muted hover:bg-destructive/20 rounded-r-lg disabled:opacity-50"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  )}
                </div>
              ))}
            </div>

            {/* Active batch upload area */}
            {batches[currentBatchIndex]?.uploaded ? (
              <div className="text-center py-6 bg-green-50 dark:bg-green-900/20 rounded-lg">
                <CheckCircle className="h-8 w-8 text-green-500 mx-auto mb-1" />
                <p className="font-medium text-sm">Batch {currentBatchIndex + 1} uploaded</p>
              </div>
            ) : (
              <>
                {/* ZIP drop zone */}
                <label
                  htmlFor="batch-files-input"
                  onDrop={handleDrop}
                  onDragOver={handleDragOver}
                  className={`border-2 border-dashed border-border hover:border-primary rounded-xl p-6 text-center block cursor-pointer transition-all hover:bg-primary/5 ${loading ? 'pointer-events-none opacity-50' : ''}`}
                >
                  <div className="flex justify-center mb-2">
                    <div className="w-10 h-10 bg-primary/10 rounded-full flex items-center justify-center">
                      <UploadIcon className="h-5 w-5 text-primary" />
                    </div>
                  </div>
                  <span className="text-primary font-medium">Choose ZIP files</span>
                  <span className="text-muted-foreground"> for Batch {currentBatchIndex + 1}</span>
                  <p className="text-xs text-muted-foreground mt-1">barcode01.zip, barcode02.zip, etc.</p>
                </label>
                <input
                  id="batch-files-input"
                  type="file"
                  multiple
                  accept=".zip"
                  onChange={handleBatchFileSelect}
                  className="hidden"
                  disabled={loading}
                />

                {/* Selected files */}
                {batches[currentBatchIndex]?.files.length > 0 && (
                  <div className="space-y-1.5 max-h-36 overflow-y-auto">
                    <p className="text-sm font-medium">{batches[currentBatchIndex].files.length} file(s) selected</p>
                    {batches[currentBatchIndex].files.map((file, i) => (
                      <div key={i} className="flex items-center justify-between bg-muted/50 px-3 py-1.5 rounded-lg">
                        <div className="flex items-center gap-2 min-w-0">
                          <File className="h-4 w-4 text-primary flex-shrink-0" />
                          <span className="text-sm truncate">{file.name}</span>
                          <span className="text-xs text-muted-foreground flex-shrink-0">({formatFileSize(file.size)})</span>
                        </div>
                        <Button variant="ghost" size="sm" onClick={() => removeFile(i)} disabled={loading}>
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    ))}
                  </div>
                )}

                {/* Per-batch metadata */}
                <div className="border-t pt-3">
                  <label className="text-sm font-medium">Batch {currentBatchIndex + 1} Metadata <span className="text-muted-foreground font-normal">(optional)</span></label>
                  <label
                    htmlFor="batch-metadata-input"
                    className={`border-2 border-dashed border-border hover:border-primary rounded-lg p-3 text-center block cursor-pointer transition-all hover:bg-primary/5 mt-1.5 ${loading ? 'pointer-events-none opacity-50' : ''}`}
                  >
                    {batches[currentBatchIndex]?.metadata ? (
                      <div className="flex items-center justify-center gap-2">
                        <CheckCircle className="h-4 w-4 text-green-500" />
                        <span className="text-sm">{batches[currentBatchIndex].metadata.name}</span>
                      </div>
                    ) : (
                      <span className="text-sm text-muted-foreground">Choose metadata CSV</span>
                    )}
                  </label>
                  <input
                    id="batch-metadata-input"
                    type="file"
                    accept=".csv"
                    onChange={handleBatchMetadataSelect}
                    className="hidden"
                    disabled={loading}
                  />
                </div>
              </>
            )}
          </CardContent>
        </Card>

        {/* Section 3: Master Metadata */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="flex items-center gap-2 text-base">
                <FileText className="h-4 w-4" />
                Master Metadata <span className="text-destructive text-sm">*</span>
              </CardTitle>
              {/* Mode switcher */}
              <div className="flex rounded-lg border overflow-hidden text-sm">
                <button
                  type="button"
                  onClick={() => setMasterMode('upload')}
                  disabled={loading}
                  className={`px-3 py-1.5 font-medium transition-colors disabled:opacity-50 ${masterMode === 'upload' ? 'bg-primary text-primary-foreground' : 'bg-muted hover:bg-muted/80'}`}
                >
                  Upload CSV
                </button>
                <button
                  type="button"
                  onClick={() => setMasterMode('create')}
                  disabled={loading}
                  className={`px-3 py-1.5 font-medium transition-colors disabled:opacity-50 ${masterMode === 'create' ? 'bg-primary text-primary-foreground' : 'bg-muted hover:bg-muted/80'}`}
                >
                  Create in app
                </button>
                <button
                  type="button"
                  onClick={async () => {
                    setMasterMode('select');
                    if (dbSheets.length === 0) {
                      setDbSheetsLoading(true);
                      try {
                        const res = await metadataAPI.listAll();
                        // Flatten all groups into master-type sheets only
                        const masterSheets = res.data.flatMap(g =>
                          g.sheets
                            .filter(s => s.type === 'master')
                            .map(s => ({ ...s, project_name: g.project_name }))
                        );
                        setDbSheets(masterSheets);
                      } catch {
                        setDbSheets([]);
                      } finally {
                        setDbSheetsLoading(false);
                      }
                    }
                  }}
                  disabled={loading}
                  className={`px-3 py-1.5 font-medium transition-colors disabled:opacity-50 ${masterMode === 'select' ? 'bg-primary text-primary-foreground' : 'bg-muted hover:bg-muted/80'}`}
                >
                  Reuse existing
                </button>
              </div>
            </div>
            <p className="text-sm text-muted-foreground">
              Required. Single CSV covering all batches — maps sample IDs to groups and treatments for R visualisation.
            </p>
          </CardHeader>
          <CardContent>
            {masterMode === 'upload' ? (
              <>
                <label
                  htmlFor="master-metadata-input"
                  className={`border-2 border-dashed border-border hover:border-primary rounded-xl p-5 text-center block cursor-pointer transition-all hover:bg-primary/5 ${loading ? 'pointer-events-none opacity-50' : ''}`}
                >
                  {masterMetadataFile ? (
                    <div className="flex items-center justify-center gap-2">
                      <CheckCircle className="h-5 w-5 text-green-500" />
                      <span className="font-medium">{masterMetadataFile.name}</span>
                    </div>
                  ) : (
                    <>
                      <FileText className="h-7 w-7 text-primary mx-auto mb-1.5" />
                      <span className="text-primary font-medium">Choose master metadata CSV</span>
                    </>
                  )}
                </label>
                <input
                  id="master-metadata-input"
                  type="file"
                  accept=".csv"
                  onChange={(e) => { const f = e.target.files[0]; if (f) setMasterMetadataFile(f); }}
                  className="hidden"
                  disabled={loading}
                />
              </>
            ) : masterMode === 'create' ? (
              /* Summary card — click "Open Editor" to launch full-screen modal */
              <>
                <div className="flex items-center justify-between px-4 py-3 border rounded-lg bg-muted/30">
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <TableProperties className="h-4 w-4" />
                    <span>{masterCols.length} columns · {masterRows.length} {masterRows.length === 1 ? 'row' : 'rows'}</span>
                  </div>
                  <Button type="button" size="sm" onClick={() => setMasterEditorOpen(true)} disabled={loading}>
                    <Pencil className="h-3.5 w-3.5 mr-1.5" />
                    Open Editor
                  </Button>
                </div>

                {/* Full-screen spreadsheet editor */}
                {masterEditorOpen && (
                  <div className="fixed inset-0 z-50 flex flex-col bg-background">

                    {/* Toolbar */}
                    <div className="flex items-center justify-between px-4 py-3 border-b bg-card shrink-0">
                      <h2 className="text-lg font-semibold flex items-center gap-2">
                        <TableProperties className="h-5 w-5" />
                        Master Metadata
                      </h2>
                      <div className="flex items-center gap-3">
                        <span className="text-sm text-muted-foreground">
                          {masterCols.length} columns · {masterRows.length} {masterRows.length === 1 ? 'row' : 'rows'}
                        </span>
                        <Button type="button" onClick={() => setMasterEditorOpen(false)}>
                          Done
                        </Button>
                      </div>
                    </div>

                    {/* Scrollable table */}
                    <div className="flex-1 overflow-auto">
                      <table className="w-full min-w-max text-sm">
                        <thead className="sticky top-0 z-10 bg-muted/95 backdrop-blur-sm">
                          <tr>
                            <th className="w-10 px-3 py-2 text-center text-xs font-mono text-muted-foreground">#</th>
                            {masterCols.map((col) => (
                              <th key={col.key} className="px-2 py-2 min-w-[140px]">
                                <div className="flex items-center gap-1">
                                  <input
                                    type="text"
                                    value={col.label}
                                    onChange={(e) => renameMasterCol(col.key, e.target.value)}
                                    disabled={col.required || col.locked}
                                    placeholder="Column name"
                                    className="flex-1 text-sm border rounded px-2 py-1 bg-background disabled:opacity-60 disabled:cursor-not-allowed"
                                  />
                                  {!col.required && (
                                    <button
                                      type="button"
                                      onClick={() => removeMasterCol(col.key)}
                                      title="Remove column"
                                      className="text-muted-foreground hover:text-destructive"
                                    >
                                      <X className="h-3.5 w-3.5" />
                                    </button>
                                  )}
                                </div>
                              </th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {masterRows.map((row, rowIdx) => (
                            <tr key={rowIdx} className="border-t hover:bg-muted/30 group">
                              <td className="px-3 py-1.5 text-center text-xs font-mono text-muted-foreground">
                                <div className="relative flex items-center justify-center h-5">
                                  <button
                                    type="button"
                                    onClick={() => removeMasterRow(rowIdx)}
                                    disabled={masterRows.length <= 1}
                                    className="absolute opacity-0 group-hover:opacity-100 hover:text-destructive disabled:opacity-0 h-5 w-5 p-0"
                                  >
                                    <X className="h-3 w-3" />
                                  </button>
                                  <span className="group-hover:hidden">{rowIdx + 1}</span>
                                </div>
                              </td>
                              {masterCols.map((col, colIdx) => (
                                <td key={col.key} className="px-2 py-1.5">
                                  <input
                                    type="text"
                                    value={row[col.key] ?? ''}
                                    onChange={(e) => updateMasterCell(rowIdx, col.key, e.target.value)}
                                    onPaste={(e) => handleMasterPaste(e, rowIdx, colIdx)}
                                    className="w-full text-sm border rounded px-2 py-1 bg-background"
                                  />
                                </td>
                              ))}
                            </tr>
                          ))}
                        </tbody>
                        <tfoot>
                          <tr className="border-t">
                            <td colSpan={masterCols.length + 1} className="px-3 py-2">
                              <Button type="button" variant="outline" size="sm" onClick={addMasterRow}>
                                <Plus className="h-3.5 w-3.5 mr-1" /> Add Row
                              </Button>
                            </td>
                          </tr>
                        </tfoot>
                      </table>
                    </div>

                    {/* Footer — add column */}
                    <div className="border-t px-4 py-2 bg-card shrink-0">
                      <Button type="button" variant="outline" size="sm" onClick={addMasterCol}>
                        <Plus className="h-3.5 w-3.5 mr-1" /> Add Column
                      </Button>
                    </div>

                  </div>
                )}
              </>
            ) : (
              /* Select from Metadata Database */
              <div className="space-y-3">
                {
                  dbSheetsLoading ? (
                    <div className="flex items-center justify-center py-8 text-muted-foreground" >
                      <Loader2 className="h-5 w-5 animate-spin mr-2" />
                      Loading sheets...
                    </div>
                  ) : dbSheets.length === 0 ? (
                    <div className="text-center py-8 text-muted-foreground text-sm space-y-3">
                      <FileText className="h-8 w-8 mx-auto opacity-40" />
                      <p>No master metadata sheets found.</p>
                      <p className="text-xs">This option reuses a sheet from a previous project. For your first project, use <strong>Upload CSV</strong> or <strong>Create in app</strong> instead.</p>
                      <div className="flex justify-center gap-2 pt-1">
                        <Button type="button" size="sm" variant="outline" onClick={() => setMasterMode('upload')}>
                          Switch to Upload CSV
                        </Button>
                        <Button type="button" size="sm" variant="outline" onClick={() => setMasterMode('create')}>
                          Switch to Create in app
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <div className="border rounded-lg divide-y overflow-hidden">
                      {dbSheets.map(sheet => (
                        <label
                          key={sheet.id}
                          className={`flex items-center gap-3 px-4 py-3 cursor-pointer hover:bg-muted/30 transition-colors ${selectedDbSheetId === sheet.id ? 'bg-primary/5 border-l-2 border-l-primary' : ''}`}
                        >
                          <input
                            type="radio"
                            name="dbSheet"
                            value={sheet.id}
                            checked={selectedDbSheetId === sheet.id}
                            onChange={() => setSelectedDbSheetId(sheet.id)}
                            disabled={loading}
                            className="accent-primary"
                          />
                          <div className="flex-1 min-w-0">
                            <p className="font-medium text-sm truncate">{sheet.name}</p>
                            <p className="text-xs text-muted-foreground">
                              {sheet.project_name} · {sheet.row_count} rows · v{sheet.version}
                            </p>
                          </div>
                          {selectedDbSheetId === sheet.id && (
                            <CheckCircle className="h-4 w-4 text-primary shrink-0" />
                          )}
                        </label>
                      ))}
                    </div>
                  )}
              </div>
            )}
          </CardContent>
        </Card >

        {/* Submission progress — shown while loading */}
        {
          loading && (
            <div className="bg-primary/10 border border-primary/20 rounded-lg p-4 space-y-3">
              <div className="flex justify-between text-sm font-medium">
                <span>{submitPhase}</span>
                <span>{uploadProgress}%</span>
              </div>
              <div className="w-full bg-muted rounded-full h-2.5 overflow-hidden">
                <motion.div
                  className="h-full bg-primary"
                  initial={{ width: 0 }}
                  animate={{ width: `${uploadProgress}%` }}
                  transition={{ duration: 0.3 }}
                />
              </div>
              {/* Per-batch status strip */}
              <div className="flex gap-1">
                {batches.map((batch, index) => (
                  <div
                    key={index}
                    className={`flex-1 h-1.5 rounded-full ${batch.uploaded ? 'bg-green-500' :
                      index === uploadingBatchIndex ? 'bg-primary animate-pulse' :
                        'bg-muted'
                      }`}
                  />
                ))}
              </div>
              <p className="text-xs text-muted-foreground text-center">
                {batches.filter(b => b.uploaded).length} of {batches.length} batches done
              </p>
            </div>
          )
        }

        {/* Start button */}
        <Button
          onClick={handleSubmit}
          disabled={loading || !projectName.trim() || !!nameError || (masterMode === 'upload' && !masterMetadataFile) || (masterMode === 'select' && !selectedDbSheetId) || (masterMode === 'create' && masterRows.every(r => Object.values(r).every(v => !v)))}
          className="w-full h-12 text-base"
        >
          {loading ? (
            <>
              <Loader2 className="mr-2 h-5 w-5 animate-spin" />
              {submitPhase || 'Working...'}
            </>
          ) : (
            <>
              <Play className="mr-2 h-5 w-5" />
              Start ({batches.length} batch{batches.length > 1 ? 'es' : ''})
            </>
          )}
        </Button>
      </div >
    </div >
  );
}
